import { FormEvent, useEffect, useRef, useState } from "react";
// import Select from "react-select";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
  getLatLng,
} from "react-google-places-autocomplete";
import { GoogleLocation } from "@/types/googleLocationTypes";
import { DropdownIcon, SearchCircleIcon } from "@/components/assets/icons/svgs";
import { CrossBlackIcon } from "@/components/assets/icons/svgs";
import aiSparkleIcon from "@/components/assets/icons/search/ai-sparkle.svg?url";
import categoryListIcon from "@/components/assets/icons/search/category-list.svg?url";
import boltIcon from "@/components/assets/icons/search/bolt.svg?url";
import clearXIcon from "@/components/assets/icons/search/clear-x.svg?url";
import searchWhiteIcon from "@/components/assets/icons/search/search-white.svg?url";
import popularStarIcon from "@/components/assets/icons/search/popular-star.svg?url";
import Select from "@/shared/select/select";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { jandiTheme } from '@/theme/jandiTheme';
import { Divider, Slider, Rating, Typography } from '@mui/material';
import { components as selectComponents } from 'react-select';
import { api } from "@/utils/api";
import React from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { trackUserAction } from '@/utils/analytics';
import { trackMetaSearch } from '@/utils/metaPixel';
import { isIOSInAppBrowser } from '@/utils/inAppBrowser';

/** Extends app-wide Jandi theme so nested providers don't reset palette/typography */
const homeFilterSliderTheme = createTheme(jandiTheme, {
  components: {
    MuiSlider: {
      styleOverrides: {
        thumb: {
          backgroundColor: '#fff',
          border: '1px solid #4285F4',
          width: 15,
          height: 15,
        },
        track: {
          backgroundColor: '#4285F4',
        },
        rail: {
          backgroundColor: '#808080',
        },
        markLabel: {
          color: '#000',
          fontSize: '12px',
          '&[data-index="0"]': {
            left: '4% !important',
          },
          '&[data-index="1"]': {
            left: '93% !important',
          },
        },
      },
    },
  },
});

// Custom Dropdown Indicator for react-select - memoized to prevent re-renders
const CustomDropdownIndicator = React.memo((props: any) => {
  return (
    <selectComponents.DropdownIndicator {...props}>
      <div 
        className="w-4 h-auto cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();

          if(props.selectProps.name === "regions") {
            props.selectProps.onRegionsOpen();
          } 
          if(props.selectProps.name === "service") {
            props.selectProps.onServiceOpen();
          }
        }}
      >
        <DropdownIcon className="w-4 h-4" />
      </div>
    </selectComponents.DropdownIndicator>
  );
});

CustomDropdownIndicator.displayName = 'CustomDropdownIndicator';

/**
 * Instagram / mobile WebViews often only register taps on the chevron,
 * or open+close instantly via touch → synthetic click. Force-open from the
 * full control using pointer/touch, and swallow the ghost click.
 */
const TouchFriendlyControl = (props: any) => {
  const { innerProps, selectProps, children } = props;
  const openedByTouchRef = React.useRef(false);

  const isIgnoredTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest("button") ||
        target.closest('[class*="clear-indicator"]') ||
        target.closest('[class*="ClearIndicator"]') ||
        target.closest('[class*="multi-value__remove"]') ||
        target.closest('[aria-label*="clear" i]') ||
        target.closest('[aria-label*="Remove" i]') ||
        target.closest('[aria-label*="Quitar" i]')
    );
  };

  const markOpenedByTouch = () => {
    openedByTouchRef.current = true;
    window.setTimeout(() => {
      openedByTouchRef.current = false;
    }, 600);
  };

  const ensureMenuOpen = () => {
    if (!selectProps.menuIsOpen && typeof selectProps.onMenuOpen === "function") {
      selectProps.onMenuOpen();
    }
  };

  const openFromTouchLike = (e: React.SyntheticEvent) => {
    if (isIgnoredTarget(e.target)) return false;
    markOpenedByTouch();
    ensureMenuOpen();
    return true;
  };

  return (
    <selectComponents.Control
      {...props}
      innerProps={{
        ...innerProps,
        style: {
          ...(innerProps?.style || {}),
          cursor: "pointer",
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        },
        // pointerdown fires reliably in Instagram WKWebView; touchend alone often does not
        onPointerDown: (e: React.PointerEvent) => {
          if (e.pointerType === "mouse" && e.button !== 0) {
            innerProps?.onPointerDown?.(e);
            return;
          }
          if (openFromTouchLike(e)) {
            // Don't preventDefault on input clears; for normal open, stop select's toggle race
            e.stopPropagation();
          }
          innerProps?.onPointerDown?.(e);
        },
        onTouchStart: (e: React.TouchEvent) => {
          openFromTouchLike(e);
          innerProps?.onTouchStart?.(e);
        },
        onTouchEnd: (e: React.TouchEvent) => {
          if (isIgnoredTarget(e.target)) {
            innerProps?.onTouchEnd?.(e);
            return;
          }
          markOpenedByTouch();
          ensureMenuOpen();
        },
        onClick: (e: React.MouseEvent) => {
          if (isIgnoredTarget(e.target)) {
            innerProps?.onClick?.(e);
            return;
          }
          if (openedByTouchRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          ensureMenuOpen();
        },
      }}
    >
      {children}
    </selectComponents.Control>
  );
};

TouchFriendlyControl.displayName = "TouchFriendlyControl";

const mobileSelectComponents = {
  Control: TouchFriendlyControl,
};


// Custom ValueContainer for horizontal scroll of selected options
const HorizontalScrollValueContainer = (props: any) => {
  const { children, getValue, selectProps } = props;
  const selectedOptions = getValue();
  // Handler to remove an option
  const handleRemove = (optionToRemove: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectProps && typeof selectProps.onChange === 'function') {
      const newValue = selectedOptions.filter((opt: any) => opt.value !== optionToRemove.value);
      selectProps.onChange(newValue, { action: 'remove-value', removedValue: optionToRemove });
    }
  };
  return (
    <selectComponents.ValueContainer {...props}>
      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          gap: '0.5rem',
          maxWidth: '100%',
          alignItems: 'center',
          minHeight: 36,
        }}
        className="custom-scrollbar"
      >
        {selectedOptions.map((option: any) => (
          <span
            key={option.value}
            className="flex items-center px-2 py-1 bg-gray-100 rounded text-xs text-gray-700"
            style={{ flex: '0 0 auto' }}
          >
            {option.label}
            <button
              type="button"
              aria-label={`Quitar ${option.label}`}
              className="ml-1 text-gray-400 hover:text-red-500 focus:outline-none"
              style={{ fontSize: '18px', lineHeight: 1, marginLeft: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={(e) => handleRemove(option, e)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {/* Render the input field */}
      {children[0]}
    </selectComponents.ValueContainer>
  );
};

type HomeFilterProps = {
  filterPayload: (payload: any) => void;
  isSearching?: boolean;
  /** Restore selects from URL after Back */
  initialFilterState?: {
    serviceId?: string;
    municipalities?: string[];
    minHourlyPay?: number | null;
    maxHourlyPay?: number | null;
    minRating?: number | null;
  } | null;
};

const HomeFilter = ({ filterPayload, isSearching, initialFilterState }: HomeFilterProps) => {

  // @ts-expect-error: Error de tipado en import.meta (no lo encuentra)
  const googlePlacesApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  const [googleLocation, setGoogleLocation] = useState<GoogleLocation>();
  const [selectedPropertyType, setSelectedPropertyType] = useState() as any;
  const modalRef: any = useRef();
  const [isOpenAdvancedFilter, setIsOpenAdvancedFilter] = useState(false);
  const [priceRange, setPriceRange] = useState<number[] | null>([null, null]);
  const [rating, setRating] = useState<number | null>(null);
  const [regionOptions, setRegionOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchMode, setSearchMode] = useState<"ai" | "category">("ai");
  const [isSearch, setIsSearch] = useState(false);
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [popularServices, setPopularServices] = useState<
    { id: string; name: string }[]
  >([]);
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: { user }) => state.user || {});
  const hydratedService = useRef(false);
  const hydratedRegions = useRef(false);
  const hydratedAdvanced = useRef(false);

  // Track which dropdown is open
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  /** Ignore blur/ghost-click closes right after a touch open (Instagram WebView). */
  const ignoreMenuCloseUntilRef = useRef(0);
  /** Fixed portals often render off-screen / untappable in Instagram iOS WebView. */
  const [useInAppMenuStrategy, setUseInAppMenuStrategy] = useState(false);

  useEffect(() => {
    setUseInAppMenuStrategy(isIOSInAppBrowser());
  }, []);

  // Open the requested dropdown (do not toggle here — closing is via onMenuClose)
  const handleDropdownOpen = (dropdownName: string) => {
    ignoreMenuCloseUntilRef.current = Date.now() + 700;
    setOpenDropdown(dropdownName);
    setIsOpenAdvancedFilter(false);
  };

  const handleDropdownClose = (dropdownName: string) => {
    if (Date.now() < ignoreMenuCloseUntilRef.current) return;
    setOpenDropdown((current) => (current === dropdownName ? null : current));
  };

  // A selection is intentional, so it must bypass the WebView ghost-close
  // guard that only protects the first tap used to open the menu.
  const closeDropdownAfterSelection = () => {
    ignoreMenuCloseUntilRef.current = 0;
    setOpenDropdown(null);
  };

  // Close dropdowns when advanced filter opens
  const handleAdvancedFilterToggle = () => {
    setIsOpenAdvancedFilter(!isOpenAdvancedFilter);
    setOpenDropdown(null); // Close any open dropdown
  };

  function valuetext(value: number) {
    return `${value}°C`;
  }

  useEffect(() => {
    const fetchDropdownData = async () => {
      const regions = await api.getRegions();
      // Grouped options: each region is a group, each municipality is an option
      const grouped = regions.map(region => ({
        label: region.name,
        options: region.municipalities.map(muni => ({
          value: `${region._id}:${muni}`,
          label: muni,
          regionId: region._id,
          regionName: region.name
        }))
      }));
      setRegionOptions(grouped);
      const { services, popular } = await api.getServices();
      setPopularServices(
        (popular || [])
          .map((service: any) => ({
            id: String(service.id || service._id || ""),
            name: String(service.name || ""),
          }))
          .filter((service: { id: string; name: string }) => service.id && service.name)
      );
      const groupedServicesMap = services.reduce((acc, service) => {
        const categoryLabel = service?.serviceCategories?.name || "Uncategorized";
        const serviceOption = {
          label: service.name,
          value: service.id || service._id,
        };

        if (!acc[categoryLabel]) {
          acc[categoryLabel] = [];
        }
        acc[categoryLabel].push(serviceOption);
        return acc;
      }, {});

      const groupedServices = Object.entries(groupedServicesMap).map(([label, options]) => ({
        label,
        options,
      }));

      setServiceOptions(groupedServices);
    };
    fetchDropdownData();
  }, [i18n.language]);

  useEffect(() => {
    if (searchMode === "ai") {
      setIsSearch(searchText.trim().length > 0);
      return;
    }
    setIsSearch(!!selectedService && selectedMunicipalities.length > 0);
  }, [searchMode, selectedService, selectedMunicipalities, searchText]);

  // Restore filter UI independently so regions don't mark hydration done before services load
  useEffect(() => {
    if (!initialFilterState) return;

    if (!hydratedService.current) {
      if (!initialFilterState.serviceId) {
        hydratedService.current = true;
      } else if (serviceOptions?.length) {
        const wanted = String(initialFilterState.serviceId);
        let matched: any = null;
        for (const group of serviceOptions as any[]) {
          matched = group.options?.find(
            (opt: any) => String(opt.value) === wanted
          );
          if (matched) break;
        }
        if (matched) setSelectedService(matched);
        hydratedService.current = true;
      }
    }

    if (!hydratedRegions.current) {
      if (!initialFilterState.municipalities?.length) {
        hydratedRegions.current = true;
      } else if (regionOptions?.length) {
        const wanted = new Set(initialFilterState.municipalities.map(String));
        const matched: any[] = [];
        for (const group of regionOptions as any[]) {
          for (const opt of group.options || []) {
            if (wanted.has(String(opt.value))) matched.push(opt);
          }
        }
        if (matched.length) setSelectedMunicipalities(matched);
        hydratedRegions.current = true;
      }
    }

    if (!hydratedAdvanced.current) {
      const minPay = initialFilterState.minHourlyPay;
      const maxPay = initialFilterState.maxHourlyPay;
      if (minPay != null || maxPay != null) {
        setPriceRange([minPay ?? null, maxPay ?? null]);
      }
      if (initialFilterState.minRating != null) {
        setRating(initialFilterState.minRating);
      }
      hydratedAdvanced.current = true;
    }
  }, [initialFilterState, serviceOptions, regionOptions]);

  const findServiceOption = (serviceId?: string | null) => {
    if (!serviceId) return null;
    const wanted = String(serviceId);
    for (const group of serviceOptions as any[]) {
      const matched = group.options?.find(
        (opt: any) => String(opt.value) === wanted
      );
      if (matched) return matched;
    }
    return null;
  };

  const findMunicipalitiesForRegion = (region: any) => {
    if (!region?._id) return [];
    const regionId = String(region._id);
    const municipalityNames = new Set(
      (region.municipalities || []).map((name: string) =>
        String(name).toLowerCase()
      )
    );
    const matched: any[] = [];
    for (const group of regionOptions as any[]) {
      for (const opt of group.options || []) {
        if (String(opt.regionId) !== regionId) continue;
        if (
          municipalityNames.size === 0 ||
          municipalityNames.has(String(opt.label).toLowerCase())
        ) {
          matched.push(opt);
        }
      }
    }
    return matched;
  };

  const pickInterpretedRegion = (interpreted: any, query: string) => {
    if (interpreted?.topRegion) return interpreted.topRegion;
    const regions = Array.isArray(interpreted?.regions)
      ? interpreted.regions
      : [];
    if (!regions.length) return null;

    const tokens = query
      .toLowerCase()
      .split(/[^a-záéíóúüñ0-9]+/i)
      .filter((token) => token.length > 2);

    const byMunicipality = regions.find((region: any) =>
      (region.municipalities || []).some((name: string) =>
        tokens.some((token) => String(name).toLowerCase().includes(token))
      )
    );
    return byMunicipality || regions[0];
  };

  const buildFilterPayload = () => {
    const payload: any = {};

    // Services (single or multi-select)
    if (selectedService) {
      payload.services = [selectedService.value];
    }

    // Regions (from selectedMunicipalities)
    if (selectedMunicipalities && selectedMunicipalities.length > 0) {
      // Use the 'regionId' of each selected municipality to get only the ID.
      payload.regions = selectedMunicipalities.map((m: any) => m.regionId);
      // Kept for URL restore (stripped before API call in hero)
      payload.municipalityValues = selectedMunicipalities.map((m: any) => m.value);
    }

    // Location (if you have a location input, e.g., GoogleLocation)
    if (googleLocation && googleLocation.value && googleLocation.value.place_id) {
      // You may need to geocode to get coordinates, but for now, just a placeholder
      // payload.location = { coordinates: [lng, lat] };
    }

    // Hourly Pay
    if (priceRange && Array.isArray(priceRange)) {
      if (priceRange[0] !== null && priceRange[0] !== undefined) payload.minHourlyPay = priceRange[0];
      if (priceRange[1] !== null && priceRange[1] !== undefined) payload.maxHourlyPay = priceRange[1];
    }

    // Minimum Rating
    if (rating !== null && rating !== undefined) {
      payload.minRating = rating;
    }

    return payload;
  };

  const applySearch = async (queryOverride?: string) => {
    try {
      let payload = buildFilterPayload();
      const query = (queryOverride ?? searchText).trim();

      if (query) {
        if (queryOverride !== undefined) {
          setSearchText(queryOverride);
        }
        setIsInterpreting(true);
        const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";
        const interpreted = await api.interpretSearch({
          query,
          limit: 5,
          lang,
        });

        if (interpreted) {
          const serviceMatch =
            findServiceOption(interpreted?.topService?._id) ||
            findServiceOption(interpreted?.services?.[0]?._id);
          const regionMatch = pickInterpretedRegion(interpreted, query);
          const municipalityMatches = findMunicipalitiesForRegion(regionMatch);

          if (serviceMatch) setSelectedService(serviceMatch);
          if (municipalityMatches.length) {
            setSelectedMunicipalities(municipalityMatches);
          }

          payload = {
            ...payload,
            ...(serviceMatch
              ? { services: [serviceMatch.value] }
              : interpreted?.topService?._id
                ? { services: [interpreted.topService._id] }
                : interpreted?.services?.[0]?._id
                  ? { services: [interpreted.services[0]._id] }
                  : {}),
            ...(municipalityMatches.length
              ? {
                  regions: municipalityMatches.map((m: any) => m.regionId),
                  municipalityValues: municipalityMatches.map(
                    (m: any) => m.value
                  ),
                }
              : regionMatch?._id
                ? { regions: [regionMatch._id] }
                : {}),
          };
        }

        trackUserAction("service_search", {
          service_id: payload.services?.[0] || "",
          service_label: query,
          user_type: user?.type || "guest",
          source: "home_filter_text",
          has_location: !!googleLocation,
          has_regions: Array.isArray(payload.regions) && payload.regions.length > 0,
          has_price_range: !!(
            priceRange &&
            (priceRange[0] !== null || priceRange[1] !== null)
          ),
          has_rating: rating !== null,
        });
        trackMetaSearch({
          search_string: query,
          content_ids: payload.services?.[0]
            ? String(payload.services[0])
            : undefined,
          content_category: "service",
        });
      } else if (selectedService) {
        trackUserAction("service_search", {
          service_id: selectedService.value,
          service_label: selectedService.label || "",
          user_type: user?.type || "guest",
          source: "home_filter",
          has_location: !!googleLocation,
          has_regions: selectedMunicipalities?.length > 0,
          has_price_range: !!(
            priceRange &&
            (priceRange[0] !== null || priceRange[1] !== null)
          ),
          has_rating: rating !== null,
        });
        trackMetaSearch({
          search_string: selectedService.label || String(selectedService.value),
          content_ids: String(selectedService.value),
          content_category: "service",
        });
      }

      console.log("Filter Payload:", payload);
      filterPayload(payload);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setIsInterpreting(false);
    }
  };

  const runSearch = () => {
    applySearch();
    setIsOpenAdvancedFilter(false);
  };

  const applyPopularSuggestion = (label: string) => {
    setSearchMode("ai");
    setSearchText(label);
    void applySearch(label);
    setIsOpenAdvancedFilter(false);
  };

  const jandiFont = { fontFamily: '"Object Sans", system-ui, sans-serif' } as const;

  return (
    <section
      className="flex flex-col w-full max-w-full md:min-w-[768px] md:max-w-[768px] gap-3 mt-4 md:mt-6 mx-auto"
      style={jandiFont}
    >
      {/* Search mode tabs — full-width on mobile per Figma "mobile deisgn tabs" */}
      <div className="flex items-center justify-center w-full" role="tablist" aria-label="Search mode">
        <div className="relative flex items-stretch w-full md:w-auto p-[5px] rounded-full border border-[#939598]/40 bg-[#f5f5f3] shadow-[inset_0_2px_4px_rgba(0,0,0,0.04)]">
          <button
            type="button"
            role="tab"
            aria-selected={searchMode === "ai"}
            onClick={() => setSearchMode("ai")}
            className={`flex flex-1 md:flex-none items-center justify-center gap-1.5 px-3 md:px-4 py-2 md:py-1.5 min-h-[44px] md:min-h-0 rounded-full text-[11px] md:text-sm font-[800] transition-colors ${
              searchMode === "ai"
                ? "bg-white text-[#4285F4]"
                : "bg-transparent text-black"
            }`}
            style={jandiFont}
          >
            <img src={aiSparkleIcon} alt="" className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" width={16} height={16} />
            <span className="text-center leading-tight whitespace-normal md:whitespace-nowrap">
              {t("searchDescribeWhatYouNeed")}
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={searchMode === "category"}
            onClick={() => setSearchMode("category")}
            className={`flex flex-1 md:flex-none items-center justify-center gap-1.5 px-3 md:px-4 py-2 md:py-1.5 min-h-[44px] md:min-h-0 rounded-full text-[11px] md:text-sm font-[800] transition-colors ${
              searchMode === "category"
                ? "bg-white text-[#4285F4]"
                : "bg-transparent text-black"
            }`}
            style={jandiFont}
          >
            <img src={categoryListIcon} alt="" className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" width={16} height={16} />
            <span className="text-center leading-tight whitespace-normal md:whitespace-nowrap">
              {t("searchByCategoryArea")}
            </span>
          </button>
        </div>
      </div>

      {searchMode === "ai" ? (
        <>
          {/* Free-text semantic search — mobile-compact button/padding */}
          <div className="flex items-center w-full bg-white border border-[#939598] rounded-full pl-4 md:pl-6 pr-1.5 md:pr-2 py-1.5 shadow-sm">
            <img src={boltIcon} alt="" className="w-5 h-5 shrink-0 mr-2 md:mr-3" width={20} height={20} />
            <input
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isSearch && !isSearching && !isInterpreting) {
                  e.preventDefault();
                  runSearch();
                }
              }}
              placeholder={t("searchDescribeNeed")}
              className="flex-1 min-w-0 border-0 outline-none bg-transparent text-sm font-semibold text-black placeholder:text-[#939598] placeholder:font-semibold py-2.5 md:py-3"
              style={jandiFont}
              aria-label={t("searchDescribeNeed")}
            />
            <div className="flex items-center gap-1 md:gap-2 shrink-0 pr-0.5 md:pr-1">
              {searchText ? (
                <button
                  type="button"
                  onClick={() => setSearchText("")}
                  className="p-2 rounded-full hover:bg-gray-100"
                  aria-label={t("clean")}
                >
                  <img src={clearXIcon} alt="" className="w-4 h-4" width={16} height={16} />
                </button>
              ) : null}
              <button
                type="button"
                className={`flex items-center justify-center rounded-full size-11 md:size-12 shrink-0 transition-colors ${
                  isSearch ? "bg-[#4285F4] hover:bg-blue-600" : "bg-gray-300"
                }`}
                aria-label="Search"
                onClick={runSearch}
                disabled={!isSearch || isSearching || isInterpreting}
              >
                {isSearching || isInterpreting ? (
                  <div className="spinner-border spinner-border-sm text-white" role="status" />
                ) : (
                  <img src={searchWhiteIcon} alt="" className="w-5 h-5" width={20} height={20} />
                )}
              </button>
            </div>
          </div>

          {/* Quick suggestions — wrap like mobile Figma */}
          {popularServices.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-1 w-full">
              <div
                className="flex items-center gap-1 text-xs font-[800] text-black/60 mr-1"
                style={jandiFont}
              >
                <img src={popularStarIcon} alt="" className="w-3.5 h-3.5" width={14} height={14} />
                <span>{t("searchPopular")}</span>
              </div>
              {popularServices.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => applyPopularSuggestion(service.name)}
                  className="px-3 py-1.5 rounded-full border border-[#939598] bg-white text-xs font-semibold text-black hover:border-[#4285F4] hover:text-[#4285F4] transition-colors whitespace-nowrap"
                  style={jandiFont}
                >
                  {service.name}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <>
          {/* Category & Area — existing filters intact */}
          <div className="hidden md:flex bg-white rounded-3xl border border-[#939598] flex-row justify-evenly gap-x-1 w-full items-center px-1">
            <div className="flex flex-1 flex-col items-start w-full md:w-auto">
              <div className="flex items-center justify-start h-12 w-full">
                <Select
                  options={serviceOptions}
                  id="service"
                  name="service"
                  value={selectedService}
                  isBorder={false}
                  placeholder={t("servicesOffered")}
                  onChange={(selectedOption) => {
                    setSelectedService(selectedOption);
                    closeDropdownAfterSelection();
                  }}
                  isClearable
                  onMenuOpen={() => handleDropdownOpen("service")}
                  onMenuClose={() => handleDropdownClose("service")}
                  menuIsOpen={openDropdown === "service"}
                  styles={{
                    control: (base) => ({
                      ...base,
                      width: "100%",
                      border: "none",
                      boxShadow: "none",
                      paddingLeft: "0",
                      textAlign: "center",
                      borderRadius: "0.8rem 0 0 0.8rem",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      whiteSpace: "nowrap",
                      paddingLeft: "0",
                      marginLeft: 0,
                      color: "#000",
                      fontWeight: "800",
                    }),
                    container: (base) => ({
                      ...base,
                      width: "100%",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      paddingLeft: "0",
                      marginLeft: 0,
                    }),
                    menu: (base) => ({
                      ...base,
                      textAlign: "left",
                      zIndex: 9999,
                      position: "absolute",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base) => ({
                      ...base,
                      textAlign: "left",
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      display: "none",
                    }),
                  }}
                />
              </div>
            </div>

            <div className="hidden md:block w-px self-stretch bg-[#939598]" />

            <div className="flex flex-1 flex-col items-start w-full md:w-auto">
              <div className="flex items-center justify-start w-full">
                <Select
                  isMulti
                  options={regionOptions}
                  id="regions"
                  name="regions"
                  value={selectedMunicipalities}
                  isBorder={false}
                  isClearable
                  placeholder={t("whereDoYouNeed")}
                  onChange={(selectedOptions) => {
                    setSelectedMunicipalities(
                      Array.isArray(selectedOptions) ? selectedOptions : []
                    );
                    closeDropdownAfterSelection();
                  }}
                  closeMenuOnSelect
                  onMenuOpen={() => handleDropdownOpen("regions")}
                  onMenuClose={() => handleDropdownClose("regions")}
                  menuIsOpen={openDropdown === "regions"}
                  styles={{
                    control: (base) => ({
                      ...base,
                      width: "100%",
                      border: "none",
                      boxShadow: "none",
                      paddingLeft: "0",
                      textAlign: "center",
                      borderRadius: "0.8rem 0 0 0.8rem",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      whiteSpace: "nowrap",
                      paddingLeft: "0",
                      marginLeft: 0,
                      color: "#000",
                      fontWeight: "800",
                    }),
                    container: (base) => ({
                      ...base,
                      width: "100%",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      paddingLeft: "0",
                      marginLeft: 0,
                    }),
                    menu: (base) => ({
                      ...base,
                      textAlign: "left",
                      zIndex: 9999,
                      position: "absolute",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base) => ({
                      ...base,
                      textAlign: "left",
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      display: "none",
                    }),
                  }}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  menuPosition="fixed"
                />
              </div>
            </div>

            <div className="hidden md:block w-px self-stretch bg-[#939598]" />

            <div className="w-full flex justify-center md:justify-end md:w-auto">
              <button
                type="button"
                className={`flex items-center justify-center rounded-full ${
                  isSearch
                    ? "bg-[#4285F4] hover:bg-blue-600"
                    : "bg-gray-300 hover:bg-gray-400"
                } transition-colors w-10 h-10`}
                aria-label="Buscar"
                onClick={runSearch}
                disabled={!isSearch || isSearching || isInterpreting}
              >
                {isSearching || isInterpreting ? (
                  <div className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  <SearchCircleIcon className="w-6 h-6 text-white" />
                )}
              </button>
            </div>
          </div>

          <div className="flex md:hidden flex-col gap-3 w-full">
            <div
              className="bg-white rounded-3xl border border-[#939598] p-1 w-full relative"
              style={{
                zIndex: openDropdown === "service" ? 40 : 1,
                overflow: "visible",
              }}
            >
              <Select
                options={serviceOptions}
                id="service-mobile"
                name="service-mobile"
                value={selectedService}
                isBorder={false}
                placeholder={t("servicesOffered")}
                onChange={(selectedOption) => {
                  setSelectedService(selectedOption);
                  closeDropdownAfterSelection();
                }}
                isClearable
                openMenuOnFocus
                openMenuOnClick
                components={mobileSelectComponents}
                menuPortalTarget={
                  useInAppMenuStrategy
                    ? null
                    : typeof document !== "undefined"
                      ? document.body
                      : undefined
                }
                menuPosition={useInAppMenuStrategy ? "absolute" : "fixed"}
                onMenuOpen={() => handleDropdownOpen("service")}
                onMenuClose={() => handleDropdownClose("service")}
                menuIsOpen={openDropdown === "service"}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: "none",
                    boxShadow: "none",
                    paddingLeft: "0",
                    minHeight: "44px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    whiteSpace: "nowrap",
                    paddingLeft: "0",
                    marginLeft: "20px",
                    color: "#000",
                    fontWeight: "800",
                    fontSize: "14px",
                    pointerEvents: "none",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    pointerEvents: "none",
                  }),
                  input: (base) => ({
                    ...base,
                    cursor: "pointer",
                  }),
                  container: (base) => ({
                    ...base,
                    width: "100%",
                    position: "relative",
                    overflow: "visible",
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    paddingLeft: "0",
                    marginLeft: 0,
                    cursor: "pointer",
                  }),
                  menu: (base) => ({
                    ...base,
                    textAlign: "left",
                    zIndex: 10050,
                    position: useInAppMenuStrategy ? "absolute" : base.position,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 10050,
                  }),
                  option: (base) => ({
                    ...base,
                    textAlign: "left",
                  }),
                  indicatorSeparator: (base) => ({
                    ...base,
                    display: "none",
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    cursor: "pointer",
                    padding: 8,
                  }),
                }}
              />
            </div>

            <div
              className="bg-white rounded-3xl border border-[#939598] p-1 w-full relative"
              style={{
                zIndex: openDropdown === "regions" ? 40 : 1,
                overflow: "visible",
              }}
            >
              <Select
                isMulti
                options={regionOptions}
                id="regions-mobile"
                name="regions-mobile"
                value={selectedMunicipalities}
                isBorder={false}
                placeholder={t("whereDoYouNeed")}
                onChange={(selectedOptions) => {
                  setSelectedMunicipalities(
                    Array.isArray(selectedOptions) ? selectedOptions : []
                  );
                  closeDropdownAfterSelection();
                }}
                closeMenuOnSelect
                openMenuOnFocus
                openMenuOnClick
                components={mobileSelectComponents}
                menuPortalTarget={
                  useInAppMenuStrategy
                    ? null
                    : typeof document !== "undefined"
                      ? document.body
                      : undefined
                }
                menuPosition={useInAppMenuStrategy ? "absolute" : "fixed"}
                onMenuOpen={() => handleDropdownOpen("regions")}
                onMenuClose={() => handleDropdownClose("regions")}
                menuIsOpen={openDropdown === "regions"}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: "none",
                    boxShadow: "none",
                    paddingLeft: "0",
                    minHeight: "44px",
                    marginLeft: "4px",
                    marginRight: "4px",
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    whiteSpace: "nowrap",
                    paddingLeft: "0",
                    marginLeft: "13px",
                    color: "#000",
                    fontWeight: "800",
                    fontSize: "14px",
                    pointerEvents: "none",
                  }),
                  input: (base) => ({
                    ...base,
                    cursor: "pointer",
                  }),
                  container: (base) => ({
                    ...base,
                    width: "100%",
                    position: "relative",
                    overflow: "visible",
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    paddingLeft: "0",
                    marginLeft: 0,
                    cursor: "pointer",
                  }),
                  menu: (base) => ({
                    ...base,
                    textAlign: "left",
                    zIndex: 10050,
                    position: useInAppMenuStrategy ? "absolute" : base.position,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 10050,
                  }),
                  option: (base) => ({
                    ...base,
                    textAlign: "left",
                  }),
                  indicatorSeparator: (base) => ({
                    ...base,
                    display: "none",
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    cursor: "pointer",
                    padding: 8,
                  }),
                }}
              />
            </div>

            <div className="flex justify-start w-full">
              <button
                type="button"
                className={`${
                  isSearch
                    ? "bg-[#4285F4] hover:bg-blue-600"
                    : "bg-gray-300 hover:bg-gray-400"
                } text-white font-bold py-2 px-7 rounded-2xl transition-colors flex items-center justify-center gap-2 text-sm`}
                onClick={runSearch}
                disabled={!isSearch || isSearching || isInterpreting}
              >
                {isSearching || isInterpreting ? (
                  <>
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>{t(`Buscando...`)}</span>
                  </>
                ) : (
                  <span>{t(`Buscar`)}</span>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default HomeFilter;